import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import type { SchedulePrintMapDay } from './schedulePrintModel';

type SchedulePrintCourtMapProps = {
  day: SchedulePrintMapDay;
};

export const SchedulePrintCourtMap = memo(function SchedulePrintCourtMap({
  day,
}: SchedulePrintCourtMapProps) {
  const { t } = useTranslation();
  const prefix = 'creativeDetail-';
  const isTimeOnY = day.orientation === 'timeOnY';

  const sectionClass = [
    'schedule-print-map-day',
    day.isFirstDay && !day.isRangeBreak ? '' : ' schedule-print-day--page-break',
    day.isRangeBreak ? ' schedule-print-map-day--range-break' : '',
  ]
    .join('')
    .trim();

  return (
    <section className={sectionClass}>
      <h2 className="schedule-print-day__title">
        {t(prefix + 'schedulePrintCourtMapTitle')}: {day.dayTitle}
        {day.dayDate ? (
          <span className="schedule-print-day__date"> — {day.dayDate}</span>
        ) : null}
        {day.rangeLabel ? (
          <span className="schedule-print-map-day__range-label">
            {' '}
            ({t(prefix + 'schedulePrintCourtRangeLabel', { courts: day.rangeLabel })})
          </span>
        ) : null}
      </h2>
      {day.legend.length > 0 ? (
        <div className="schedule-print-map-legend" aria-label={t(prefix + 'schedulePrintLegendTitle')}>
          <span className="schedule-print-map-legend__title">
            {t(prefix + 'schedulePrintLegendTitle')}
          </span>
          <ul className="schedule-print-map-legend__list">
            {day.legend.map((entry) => (
              <li key={entry.classId} className="schedule-print-map-legend__item">
                <span
                  className="schedule-print-map-legend__swatch"
                  style={{ backgroundColor: entry.color }}
                />
                <span>{entry.className}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {day.courtBands.map((band, bandIndex) => (
        <div
          key={`${day.dayKey}-band-${band.startIndex}-${day.rangeLabel}`}
          className={[
            'schedule-print-map',
            isTimeOnY ? 'schedule-print-map--time-vertical' : '',
            bandIndex > 0 ? 'schedule-print-map--court-band-break' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {isTimeOnY ? (
            <SchedulePrintCourtMapTimeOnY day={day} band={band} />
          ) : (
            <SchedulePrintCourtMapTimeOnX day={day} band={band} />
          )}
        </div>
      ))}
    </section>
  );
});

type BandSlice = { startIndex: number; endIndex: number };

function SchedulePrintCourtMapTimeOnX({
  day,
  band,
}: {
  day: SchedulePrintMapDay;
  band: BandSlice;
}) {
  const { t } = useTranslation();
  const prefix = 'creativeDetail-';

  return (
    <>
      <div className="schedule-print-map__header-row">
        <div className="schedule-print-map__court-axis">
          {t(prefix + 'schedulePrintCourtAxis')}
        </div>
        <div className="schedule-print-map__time-axis">
          <span className="schedule-print-map__time-axis-label">
            {t(prefix + 'schedulePrintTimeAxis')}
          </span>
          <div className="schedule-print-map__timeline-header">
            {day.hourTicks.map((tick) => (
              <span
                key={`${day.dayKey}-tick-${tick.label}`}
                className="schedule-print-map__hour-tick"
                style={{ left: `${tick.leftPercent}%` }}
              >
                {tick.label}
              </span>
            ))}
          </div>
        </div>
      </div>
      {day.courtRows.slice(band.startIndex, band.endIndex).map((court, rowInBand) => {
        const courtRowIndex = band.startIndex + rowInBand;
        const rowBlocks = day.blocks.filter((b) => b.courtRowIndex === courtRowIndex);
        return (
          <div key={court.courtId} className="schedule-print-map__court-row">
            <div className="schedule-print-map__court-label">{court.courtName}</div>
            <div className="schedule-print-map__timeline">
              {day.hourTicks.map((tick) => (
                <span
                  key={`${court.courtId}-grid-${tick.label}`}
                  className="schedule-print-map__grid-line"
                  style={{ left: `${tick.leftPercent}%` }}
                />
              ))}
              {rowBlocks.map((block) => (
                <div
                  key={block.matchId}
                  className="schedule-print-map__block"
                  style={{
                    left: `${block.leftPercent}%`,
                    width: `${block.widthPercent}%`,
                    backgroundColor: block.classColor,
                  }}
                  title={`${block.shortLabel} — ${block.className}`}
                >
                  <span className="schedule-print-map__block-label">{block.shortLabel}</span>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </>
  );
}

function SchedulePrintCourtMapTimeOnY({
  day,
  band,
}: {
  day: SchedulePrintMapDay;
  band: BandSlice;
}) {
  const { t } = useTranslation();
  const prefix = 'creativeDetail-';
  const bandCourts = day.courtRows.slice(band.startIndex, band.endIndex);

  return (
    <div className="schedule-print-map__vertical-body">
      <div className="schedule-print-map__vertical-header">
        <div className="schedule-print-map__time-axis-corner">
          {t(prefix + 'schedulePrintTimeAxis')}
        </div>
        {bandCourts.map((court) => (
          <div key={court.courtId} className="schedule-print-map__court-column-header">
            {court.courtName}
          </div>
        ))}
      </div>
      <div className="schedule-print-map__vertical-grid">
        <div className="schedule-print-map__time-axis-vertical">
          {day.hourTicks.map((tick) => (
            <span
              key={`${day.dayKey}-vtick-${tick.label}`}
              className="schedule-print-map__hour-tick-vertical"
              style={{ top: `${tick.topPercent}%` }}
            >
              {tick.label}
            </span>
          ))}
        </div>
        {bandCourts.map((court, colInBand) => {
          const courtRowIndex = band.startIndex + colInBand;
          const colBlocks = day.blocks.filter((b) => b.courtRowIndex === courtRowIndex);
          return (
            <div key={court.courtId} className="schedule-print-map__court-column">
              {day.hourTicks.map((tick) => (
                <span
                  key={`${court.courtId}-vgrid-${tick.label}`}
                  className="schedule-print-map__grid-line-vertical"
                  style={{ top: `${tick.topPercent}%` }}
                />
              ))}
              {colBlocks.map((block) => (
                <div
                  key={block.matchId}
                  className="schedule-print-map__block schedule-print-map__block--vertical"
                  style={{
                    top: `${block.topPercent}%`,
                    height: `${block.heightPercent}%`,
                    backgroundColor: block.classColor,
                  }}
                  title={`${block.shortLabel} — ${block.className}`}
                >
                  <span className="schedule-print-map__block-label">{block.shortLabel}</span>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
